/**
 * Offline Data Sync Hook
 * Handles offline storage and automatic sync for irrigation operations
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const DB_NAME = 'centinela-offline-db';
const DB_VERSION = 1;
const STORE_NAME = 'irrigation_operations';

// Open IndexedDB
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { 
          keyPath: 'localId', 
          autoIncrement: true 
        });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Save operation locally
const saveLocally = async (operation) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const data = {
      ...operation,
      synced: false,
      timestamp: Date.now(),
      localId: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    const request = store.add(data);
    request.onsuccess = () => resolve(data);
    request.onerror = () => reject(request.error);
  });
};

// Get unsynced operations
const getUnsyncedOperations = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.getAll(false);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Mark operation as synced
const markAsSynced = async (localId, serverId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(localId);
    
    request.onsuccess = () => {
      const data = request.result;
      if (data) {
        data.synced = true;
        data.serverId = serverId;
        store.put(data);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

// Delete synced operations older than 7 days
const cleanupOldOperations = async () => {
  const db = await openDB();
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.synced && cursor.value.timestamp < sevenDaysAgo) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
};

export function useOfflineSync(apiEndpoint, token) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const unsynced = await getUnsyncedOperations();
      setPendingCount(unsynced.length);
    } catch (error) {
      console.error('Error getting pending count:', error);
    }
  }, []);

  // Sync pending operations
  const syncPendingOperations = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    
    setIsSyncing(true);
    
    try {
      const unsynced = await getUnsyncedOperations();
      
      if (unsynced.length === 0) {
        setIsSyncing(false);
        return;
      }
      
      console.log(`[OfflineSync] Syncing ${unsynced.length} operations...`);
      let syncedCount = 0;
      
      for (const operation of unsynced) {
        try {
          const response = await fetch(`${apiEndpoint}/irrigation/operations/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ...operation,
              offline_created: true,
              offline_timestamp: operation.timestamp
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            await markAsSynced(operation.localId, result.id);
            syncedCount++;
          }
        } catch (error) {
          console.error('Error syncing operation:', error);
        }
      }
      
      if (syncedCount > 0) {
        toast.success(`${syncedCount} operaciones sincronizadas`);
        setLastSyncTime(Date.now());
      }
      
      await updatePendingCount();
      await cleanupOldOperations();
      
    } catch (error) {
      console.error('Sync error:', error);
      // Silenced - no toast to avoid annoying users
    } finally {
      setIsSyncing(false);
    }
  }, [apiEndpoint, token, isOnline, isSyncing, updatePendingCount]);

  // Save operation (online or offline)
  const saveOperation = useCallback(async (operationData) => {
    if (isOnline) {
      try {
        const response = await fetch(`${apiEndpoint}/irrigation/operations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(operationData)
        });
        
        if (response.ok) {
          return { success: true, offline: false, data: await response.json() };
        }
        throw new Error('Server error');
        
      } catch (error) {
        // Fall back to offline storage
        console.log('[OfflineSync] Online save failed, saving offline');
      }
    }
    
    // Save offline
    try {
      const saved = await saveLocally(operationData);
      await updatePendingCount();
      toast.info('Guardado offline. Se sincronizará al recuperar conexión.');
      return { success: true, offline: true, data: saved };
    } catch (error) {
      console.error('Error saving offline:', error);
      toast.error('Error al guardar');
      return { success: false, error };
    }
  }, [apiEndpoint, token, isOnline, updatePendingCount]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Silenced - connection restored toast
      // Trigger sync after a short delay
      setTimeout(syncPendingOperations, 1000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      // Silenced - offline mode toast
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Listen for service worker sync messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_SUCCESS') {
          updatePendingCount();
          // Silenced
        }
      });
    }
    
    // Initial pending count
    updatePendingCount();
    
    // Auto-sync every 30 seconds when online
    const syncInterval = setInterval(() => {
      if (navigator.onLine) {
        syncPendingOperations();
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(syncInterval);
    };
  }, [syncPendingOperations, updatePendingCount]);

  // Force sync
  const forceSync = useCallback(() => {
    if (navigator.onLine) {
      syncPendingOperations();
      
      // Also trigger service worker sync
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.sync.register('sync-offline-data');
        });
      }
    } else {
      // Silenced - no connection warning
    }
  }, [syncPendingOperations]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    saveOperation,
    forceSync,
    syncPendingOperations
  };
}

export default useOfflineSync;
